// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useCallback, useEffect, useState, useRef } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import CustomSnackbar from "../ui/Snackbars";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoIcon from "@mui/icons-material/Info";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useHttp from "../../utils/http";
import {
  getInvoiceCostTypes,
  getTravelInvoices,
  postTravelInvoices,
  patchApproveInvoices,
  patchRejectInvoices,
  postPushInvoicesByJobNumber,
  getTotalPerDiemPaymentsCount,
} from "../../utils/end-points";
import { isAccessGranted } from "../../utils/oauth";
import { makeStyles } from "@mui/styles";
import { MenuItem, Select } from "@mui/material";
import { PRIVILEGES } from "../../Config.js";
import { styled } from "@mui/material/styles";
import {
  currencyTypes,
  invoiceCurrencyTypes,
  invoiceApproval,
  monthsArray,
  travelInvoiceNumberRegex,
  travelStatus,
  USDCurrency,
  currencyInvoiceCostType
} from "../../utils/const";
import {
  getFinanceApprovalStatusInfo,
  getHrApprovalStatusInfo,
  getNetSuiteStatusInfo,
} from "../../theme/status-colors";
import "../../css/Invoices.scss";

const useStyles = makeStyles(() => ({
  paper: { minWidth: "550px" },
}));

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const headCells = [
  {
    id: "invoice_number",
    label: "Invoice Number",
    align: "left",
    disablePadding: true,
  },
  { id: "month", label: "Month", align: "left", disablePadding: true },
  { id: "cost_type", label: "Cost Type", align: "left", disablePadding: true },
  {
    id: "invoice_amount",
    label: "Invoice Amount",
    align: "left",
    disablePadding: true,
  },
  {
    id: "invoice_currency",
    label: "Invoice Currency",
    align: "left",
    disablePadding: true,
  },
  {
    id: "converted_amount",
    label: "Paid Amount",
    align: "left",
    disablePadding: true,
  },
  {
    id: "converted_currency",
    label: "Paid Currency",
    align: "left",
    disablePadding: true,
  },
  {
    id: "hr_approval",
    label: "HR Approval",
    align: "center",
    disablePadding: true,
  },
  {
    id: "finance_approval",
    label: "Finance Approval",
    align: "center",
    disablePadding: true,
  },
  {
    id: "net_suite_status",
    label: "NS Status",
    align: "center",
    disablePadding: true,
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell
          className="Table-head-cell-checkbox"
          padding="checkbox"
        ></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="Head-label"
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const Invoices = (props) => {
  const { travel, fetchPerdiemPaymentSummary,isLoading, setIsLoading } = props;
  const classes = useStyles();
  const { handleRequest } = useHttp();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceCostTypes, setInvoiceCostTypes] = useState([]);
  const [rejectReason, setRejectReason] = useState(null);
  const [showRejectReasonDialog, setShowRejectReasonDialog] = useState(false);
  const [isSaveEnable, setSaveEnable] = useState(false);
  const [isResetEnable, setResetEnable] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [confirmationDialogData, setConfirmationDialogData] = useState({
    open: false,
    title: "",
    content: "",
    acceptLabel: "",
    declineLabel: "",
    acceptAlert: null,
    declineAlert: null,
  });
  const [paymentCount, setPaymentCount] = useState(0);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  useEffect(() => {
    fetchInvoices(false);
  }, []);

  const fetchInvoices = useCallback(async (showSuccessSnack) => {
    setIsLoading(true);
    setEditingRowId(null);
    setResetEnable(false);
    setSaveEnable(false);
    const promises = [];
    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getTravelInvoices.replace("$jobNumber", travel.jobNumber),
          "GET",
          null,
          (invoices) => {
            if (invoices) {
              setInvoices(invoices);
              resolve();
            } else {
              setSnackbarData({
                message: "Error while fetching invoices",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message: error,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );
    if (isAccessGranted([PRIVILEGES.ADD_TRAVEL_INVOICES])) {
      promises.push(
        new Promise(function (resolve, reject) {
          handleRequest(
            getInvoiceCostTypes,
            "GET",
            null,
            (costTypes) => {
              if (costTypes) {
                setInvoiceCostTypes(costTypes);
                resolve();
              } else {
                setSnackbarData({
                  message: "Error while fetching invoice cost types",
                  open: true,
                  severity: "error",
                  onClose: onCloseSnackbar,
                });
                reject();
              }
            },
            (error) => {
              setSnackbarData({
                message: error,
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          );
        })
      );
    }
     if (isAccessGranted([PRIVILEGES.ADD_TRAVEL_CARDS])) {
        // Fetch total per diem payments count
        promises.push(
            new Promise((resolve, reject) => {
                handleRequest(
                    getTotalPerDiemPaymentsCount.replace("$jobNumber", travel.jobNumber),
                    "GET",
                    null,
                    (paymentCount) => {
                        if (paymentCount !== undefined) {
                            setPaymentCount(paymentCount);
                            resolve();
                        } else {
                            setSnackbarData({
                                message: "Error while fetching total per diem payments count",
                                open: true,
                                severity: "error",
                                onClose: onCloseSnackbar,
                            });
                            reject();
                        }
                    },
                    (error) => {
                        setSnackbarData({
                            message: error,
                            open: true,
                            severity: "error",
                            onClose: onCloseSnackbar,
                        });
                        reject();
                    }
                );
            })
        );
      }

    Promise.all(promises)
      .then(() => {
        if (showSuccessSnack) {
          setSnackbarData({
            message: "Success",
            open: true,
            severity: "success",
            onClose: onCloseSnackbar,
          });
        }
      })
      .catch((error) => {
        setSnackbarData({
          message: "Failed",
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const selectInvoice = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(
        selectedInvoices.filter((invoice) => invoice !== invoiceId)
      );
    } else {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
    }
  };

  // adding new invoice by validating existing invoice
  const validateAndAddInvoice = () => {
    setSaveEnable(false);
    // validating last added invoice information
    const newInvoices = invoices.filter(
      (invoice) => invoice.isNewInvoice === true
    );
    if (newInvoices.length > 0) {
      let lastAddedInvoice = newInvoices[newInvoices.length - 1];
      lastAddedInvoice = validateInvoiceInformation(lastAddedInvoice, true);
      if (!lastAddedInvoice.isError) {
        setInvoices([...invoices, createNewInvoice()]);
      } else {
        setSaveEnable(false);
      }
    } else {
      let invoiceObj = createNewInvoice();
      setInvoices([...invoices, invoiceObj]);
    }
  };

  // creating new invoice object
  const createNewInvoice = () => {
    setEditingRowId(invoices.length);
    // creating new invoice object
    const invoiceObj = {
      invoiceNumber: "",
      month: "",
      invCostTypeId: null,
      invoiceCurrency: "",
      invoiceAmount: "",
      convertedCurrency: USDCurrency,
      convertedAmount: "",
      isNewInvoice: true,
      isError: false,
      status: invoiceApproval.PENDING,
      jobNumber: travel.jobNumber,
    };

    setResetEnable(true);
    return invoiceObj;
  };

  // function to validate information of a invoice object
  const validateInvoiceInformation = (invoiceObj, showError) => {
    invoiceObj.isError = false;
    let errorMessage = "Please re-check the information. ";

    if (
      !invoiceObj.invoiceNumber ||
      !invoiceObj.month ||
      !invoiceObj.invCostTypeId ||
      !invoiceObj.invoiceCurrency ||
      !invoiceObj.invoiceAmount ||
      !invoiceObj.convertedCurrency ||
      !invoiceObj.convertedAmount
    ) {
      let errorCount = 0;
      invoiceObj.isError = true;

      if (showError) {
        if (!invoiceObj.invoiceNumber) {
          errorMessage = errorMessage.concat("Invoice Number, ");
          errorCount += 1;
        }
        if (!invoiceObj.month) {
          errorMessage = errorMessage.concat("Month, ");
          errorCount += 1;
        }
        if (!invoiceObj.invCostTypeId) {
          errorMessage = errorMessage.concat("Cost Type, ");
          errorCount += 1;
        }
        if (!invoiceObj.invoiceCurrency) {
          errorMessage = errorMessage.concat("Invoice Currency, ");
          errorCount += 1;
        }
        if (!invoiceObj.invoiceAmount) {
          errorMessage = errorMessage.concat("Invoice Amount, ");
          errorCount += 1;
        }
        if (!invoiceObj.convertedCurrency) {
          errorMessage = errorMessage.concat("Paid Currency, ");
          errorCount += 1;
        }
        if (!invoiceObj.convertedAmount) {
          errorMessage = errorMessage.concat("Paid Amount, ");
          errorCount += 1;
        }

        errorMessage = errorMessage.slice(0, -2);
        if (errorCount > 1) {
          errorMessage = errorMessage.concat(" are mandatory.");
        } else {
          errorMessage = errorMessage.concat(" is mandatory.");
        }
        setSnackbarData({
          message: errorMessage,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      } else {
        setSnackbarData({
          message: "",
          open: false,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }

    // Check if cost type is "currency" and payment is greater than 0
    if (invoiceObj.invCostTypeId === currencyInvoiceCostType && paymentCount > 0) {
      invoiceObj.isError = true;
      setSnackbarData({
          message: "The finance team has started processing the payments. You cannot add currency cost type invoices at this time.",
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
      });
  }

    if (!travelInvoiceNumberRegex.test(invoiceObj.invoiceNumber)) {
      invoiceObj.isError = true;
      setSnackbarData({
        message:
          "'" +
          invoiceObj.invoiceNumber +
          "' is an invalid invoice number. Only numbers, alphabets, hyphen and underscore are allowed.",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    }
    return invoiceObj;
  };

  const getEditField = (id, fieldName) => {
    switch (fieldName) {
      case "invoiceNumber":
        return (
          <TextField
            variant="outlined"
            size="small"
            className="Invoice-input"
            value={invoices[id][fieldName] ? invoices[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );

      case "month":
        return (
          <Select
            value={invoices[id][fieldName]}
            fullWidth
            className="Invoice-input"
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
            size="small"
          >
            {monthsArray.length > 0 &&
              monthsArray.map((month) => {
                return (
                  <MenuItem key={month} value={month}>
                    <span>{month}</span>
                  </MenuItem>
                );
              })}
          </Select>
        );

      case "invCostTypeId":
        return (
          <Select
            value={invoices[id][fieldName]}
            fullWidth
            className="Invoice-input"
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
            size="small"
          >
            {invoiceCostTypes.length > 0 &&
              invoiceCostTypes.map((type) => {
                return (
                  <MenuItem key={type.id} value={type.id}>
                    <span>{type.name}</span>
                  </MenuItem>
                );
              })}
          </Select>
        );

      case "invoiceCurrency":
        return (
          <Select
            value={invoices[id][fieldName]}
            fullWidth
            className="Invoice-input"
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
            size="small"
          >
            {invoiceCurrencyTypes.length > 0 &&
              invoiceCurrencyTypes.map((currency) => {
                return (
                  <MenuItem key={currency[0]} value={currency[0]}>
                    <span>{currency[0] + " (" + currency[1] + ")"}</span>
                  </MenuItem>
                );
              })}
          </Select>
        );

      case "convertedCurrency":
        return (
          <Select
            value={invoices[id][fieldName]}
            fullWidth
            className="Invoice-input"
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
            size="small"
          >
            {currencyTypes.length > 0 &&
              currencyTypes.map((currency) => {
                return (
                  <MenuItem key={currency[0]} value={currency[0]}>
                    <span>{currency[0] + " (" + currency[1] + ")"}</span>
                  </MenuItem>
                );
              })}
          </Select>
        );

      case "invoiceAmount":
        return (
          <TextField
            variant="outlined"
            size="small"
            type="number"
            className="Invoice-input"
            value={invoices[id][fieldName] ? invoices[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(parseFloat(event.target.value), id, fieldName)
            }
          />
        );

      case "convertedAmount":
        return (
          <TextField
            variant="outlined"
            size="small"
            type="number"
            className="Invoice-input"
            value={invoices[id][fieldName] ? invoices[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(parseFloat(event.target.value), id, fieldName)
            }
          />
        );

      case "hrApproval":
        return getHrApprovalInfo(invoices[id]);

      case "financeApproval":
        return getFinanceApprovalInfo(invoices[id]);

      case "netSuiteStatus":
        return getNetSuiteStatus(invoices[id]);

      default:
        return (
          <TextField
            variant="outlined"
            size="small"
            disabled
            value={invoices[id][fieldName] ? invoices[id][fieldName] : ""}
            onChange={(event) =>
              setEditFieldValue(event.target.value, id, fieldName)
            }
          />
        );
    }
  };

  const setEditFieldValue = (value, id, fieldName) => {
    let invoiceObj = invoices[id];
    invoiceObj[fieldName] = value;
    invoiceObj = validateInvoiceInformation(invoiceObj, false);
    if (invoiceObj.isError) {
      setSaveEnable(false);
    } else {
      let invoiceArr = [...invoices];
      invoiceArr[id] = invoiceObj;
      setInvoices(invoiceArr);
      setSaveEnable(true);
    }
  };

  const resetInvoices = () => {
    setConfirmationDialogData({
      open: true,
      title: "Reset invoices",
      content: "Are you sure you want reset invoices into last saved values?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptResetAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  const saveInvoices = () => {
    setConfirmationDialogData({
      open: true,
      title: "Save invoices",
      content: "Are you sure you want to save these invoices?",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handleAcceptSaveAlert,
      declineAlert: handleDeclineAlert,
    });
  };

  // const deleteInvoices = (perdiemObj, key) => {
  //     setConfirmationDialogData({
  //         open: true,
  //         title: "Delete invoices",
  //         content: "Are you sure you want to delete these invoices?",
  //         acceptLabel: "Yes",
  //         declineLabel: "No",
  //         acceptAlert: () => handleAcceptDeleteAlert(perdiemObj, key),
  //         declineAlert: handleDeclineAlert
  //     })
  // }

  const handleAcceptResetAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    fetchInvoices(false);
  };

  const handleAcceptSaveAlert = useCallback(async () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);
    const newInvoices = invoices.filter(
      (invoice) => invoice.isNewInvoice === true
    );

    handleRequest(
      postTravelInvoices.replace("$jobNumber", travel.jobNumber),
      "POST",
      newInvoices,
      (response) => {
        if (response) {
          fetchInvoices(true);
        } else {
          setIsLoading(false);
          setSnackbarData({
            message: "Error while inserting invoices",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  });

  const handleAcceptDeleteAlert = useCallback(async (perdiemObj, id) => {
    // setConfirmationDialogData(data => ({ ...data, open: false }))
    // if(perdiemObj?.isNewPerdiem && perdiemObj.isNewPerdiem) {
    //     let perdiemAllocations = invoices;
    //     perdiemAllocations = perdiemAllocations.filter((perdiem, key) => key !== id)
    //     setinvoices(perdiemAllocations)
    // }
    // else{
    //     setIsLoading(true)
    //     handleRequest(deletePerdiemAllocation.replace("$id", perdiemObj.id), "DELETE", null, (deletedId) => {
    //         if (deletedId) {
    //             let perdiemAllocations = invoices;
    //             perdiemAllocations = perdiemAllocations.filter((perdiem, key) => key !== id)
    //             setinvoices(perdiemAllocations)
    //             setEditingRowId(editingRowId-1)
    //             setIsLoading(false)
    //         }
    //         else {
    //             setSnackbarData({ message: "Error while deleting perdiem allocation", open: true, severity: "error", onClose: onCloseSnackbar })
    //         }
    //     }, (error) => {
    //         setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
    //     })
    // }
  });

  const getHrApprovalInfo = (invoice) => {
    let statusChip = getHrApprovalStatusInfo(invoice.status);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
        <span className="Info-icon">
          {invoice.status === invoiceApproval.REJECTED_BY_TRAVEL && (
            <HtmlTooltip
              title={
                <>
                  <Typography variant="subtitle1" color="inherit">
                    Rejected Reason:
                  </Typography>
                  <Typography variant="subtitle2">
                    {invoice.rejectReason}
                  </Typography>
                </>
              }
            >
              <InfoIcon size="small" />
            </HtmlTooltip>
          )}
        </span>
      </span>
    );
  };

  const getFinanceApprovalInfo = (invoice) => {
    let statusChip = getFinanceApprovalStatusInfo(invoice.status);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
        <span className="Info-icon">
          {invoice.status === invoiceApproval.REJECTED_BY_FINANCE && (
            <HtmlTooltip
              title={
                <>
                  <Typography variant="subtitle1" color="inherit">
                    Rejected Reason:
                  </Typography>
                  <Typography variant="subtitle2">
                    {invoice.rejectReason}
                  </Typography>
                </>
              }
            >
              <InfoIcon size="small" />
            </HtmlTooltip>
          )}
        </span>
      </span>
    );
  };

  const getNetSuiteStatus = (invoice) => {
    let statusChip = getNetSuiteStatusInfo(invoice.status);
    return (
      <span>
        <span
          className="Status-chip"
          style={{
            backgroundColor: statusChip[1],
            color: statusChip[2],
          }}
        >
          {statusChip[0]}
        </span>
      </span>
    );
  };

  const getCostType = (invoice) => {
    if (invoice?.costTypeName) {
      return invoice.costTypeName;
    } else if (
      invoice?.invCostTypeId &&
      invoiceCostTypes.filter(
        (costType) => costType.id === invoice.invCostTypeId
      ).length > 0
    ) {
      return invoiceCostTypes.filter(
        (costType) => costType.id === invoice.invCostTypeId
      )[0]?.name;
    }
    return "";
  };

  const isSelectDisabled = (status) => {
    // disable buttons if travel is not In progress or Extended
    if (
      !(
        travel.status === travelStatus.INPROGRESS ||
        travel.status === travelStatus.EXTENDED
      )
    ) {
      return true;
    }
    if (isAccessGranted([PRIVILEGES.INVOICE_HR_APPROVAL])) {
      if (status === invoiceApproval.PENDING) {
        return false;
      }
      return true;
    } else if (isAccessGranted([PRIVILEGES.INVOICE_FINANCE_APPROVAL])) {
      if (status === invoiceApproval.ACCEPTED_BY_TRAVEL) {
        return false;
      }
      return true;
    }
    return true;
  };

  const handleInvoice = (isApproved) => {
    var title = "Approve Invoice";
    var content = "Are you sure you want to approve these invoice(s)?";
    if (isApproved) {
      setConfirmationDialogData({
        open: true,
        title: title,
        content: content,
        acceptLabel: "Yes",
        declineLabel: "No",
        acceptAlert: handleApproveInvoiceAlert,
        declineAlert: handleDeclineAlert,
      });
    } else {
      setShowRejectReasonDialog(true);
    }
  };

  // The note regarding duplicate invoice numbers can be removed after old invoices are all added
  const handlePushInvoicesToNS = () => {
    setConfirmationDialogData({
      open: true,
      title: "Push Invoice NS",
      content:
        "Are you sure you want to push approved invoices to Net Suite? Please note that if there are duplicate invoice numbers, only the first invoice will be pushed to Net Suite.",
      acceptLabel: "Yes",
      declineLabel: "No",
      acceptAlert: handlePushInvoices,
      declineAlert: handleDeclineAlert,
    });
  };

  const handlePushInvoices = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);
    handleRequest(
      postPushInvoicesByJobNumber.replace("$jobNumber", travel.jobNumber),
      "POST",
      {},
      (invoiceResult) => {
        if (invoiceResult) {
          if (invoiceResult.failedInvoices?.length > 0) {
            setSnackbarData({
              message: `Error while pushing following invoices to Net Suite: 
                        ${invoiceResult.failedInvoices
                          .map((item) => item.invoiceNumber)
                          .join(", ")}`,
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            fetchInvoices(false);
          } else {
            fetchInvoices(true);
            fetchPerdiemPaymentSummary();
          }
        } else {
          setIsLoading(false);
          setSnackbarData({
            message: "Error while pushing invoices to Net Suite",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  const handleApproveInvoiceAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
    setIsLoading(true);
    let body = {
      invoiceIds: selectedInvoices,
    };
    handleRequest(
      patchApproveInvoices,
      "PATCH",
      body,
      (invoiceIds) => {
        if (invoiceIds) {
          setSelectedInvoices([]);
          fetchInvoices(true);
        } else {
          setIsLoading(false);
          setSnackbarData({
            message: "Error while approving invoices",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      },
      (error) => {
        setIsLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  };

  const handleRejectInvoiceReason = (event) => {
    setRejectReason(event.target.value);
  };

  const rejectInvoice = (event) => {
    if (rejectReason === null || rejectReason === "") {
      setSnackbarData({
        message: "Please enter a reject reason",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    } else {
      setShowRejectReasonDialog(false);
      setIsLoading(true);
      let body = {
        invoiceIds: selectedInvoices,
        rejectReason: rejectReason,
      };
      handleRequest(
        patchRejectInvoices,
        "PATCH",
        body,
        (invoiceIds) => {
          if (invoiceIds) {
            setSelectedInvoices([]);
            fetchInvoices(true);
          } else {
            setIsLoading(false);
            setSnackbarData({
              message: "Error while rejecting invoices",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
        },
        (error) => {
          setIsLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    }
  };

  const cancelInvoiceRejection = (event) => {
    setRejectReason("");
    setShowRejectReasonDialog(false);
  };

  const handleDeclineAlert = () => {
    setConfirmationDialogData((data) => ({ ...data, open: false }));
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  return (
    <div className="Invoice">
      <Dialog
        onClose={() => setShowRejectReasonDialog(false)}
        open={showRejectReasonDialog}
        classes={{ paper: classes.paper }}
        className="Reject-reason-dialog"
      >
        <div className="Dialog-div">
          <Typography variant="h4" gutterBottom className="Title">
            Reject Invoice
          </Typography>

          <Typography gutterBottom className="Description">
            Please make sure you want to reject these invoice(s). This can not
            be undone.
          </Typography>

          <TextField
            size="small"
            fullWidth
            placeholder="Reason to reject"
            className="Textfield"
            onChange={handleRejectInvoiceReason}
          />

          <Grid item lg={12} className="Button-div">
            <Button
              variant="contained"
              color="secondary"
              className="Reject-button"
              onClick={rejectInvoice}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className="Cancel-button"
              onClick={cancelInvoiceRejection}
            >
              Cancel
            </Button>
          </Grid>
        </div>
      </Dialog>

      <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
      <CustomSnackbar snackbar={snackbarData} />
      {isAccessGranted([
        PRIVILEGES.INVOICE_FINANCE_APPROVAL,
        PRIVILEGES.INVOICE_HR_APPROVAL,
      ]) && (
        <Grid container className="Button-grid-approvals">
          <Grid item lg={9}>
            {isAccessGranted([PRIVILEGES.INVOICE_HR_APPROVAL]) ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  className="Approve-button"
                  startIcon={<CheckCircleOutlineIcon />}
                  disabled={selectedInvoices.length === 0}
                  onClick={() => handleInvoice(true)}
                >
                  Approve (HR)
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  className="Reject-button"
                  startIcon={<HighlightOffIcon />}
                  disabled={selectedInvoices.length === 0}
                  onClick={() => handleInvoice(false)}
                >
                  Reject (HR)
                </Button>
              </>
            ) : isAccessGranted([PRIVILEGES.INVOICE_FINANCE_APPROVAL]) ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  className="Approve-button"
                  startIcon={<CheckCircleOutlineIcon />}
                  disabled={selectedInvoices.length === 0}
                  onClick={() => handleInvoice(true)}
                >
                  Approve (Finance)
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  className="Reject-button"
                  startIcon={<HighlightOffIcon />}
                  disabled={selectedInvoices.length === 0}
                  onClick={() => handleInvoice(false)}
                >
                  Reject (Finance)
                </Button>
              </>
            ) : (
              <></>
            )}
          </Grid>
          <Grid item lg={3} className="Button-grid-netsuite">
            {isAccessGranted([PRIVILEGES.PUSH_INVOICES_TO_NS]) && (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Push-to-netsuite-button"
                startIcon={<CurrencyExchangeIcon />}
                disabled={
                  invoices.filter(
                    (invoice) =>
                      invoice.status === invoiceApproval.ACCEPTED_BY_FINANCE
                  ).length === 0
                }
                onClick={handlePushInvoicesToNS}
              >
                Push Invoices to NS
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      <div sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer className="Table-container" sx={{ maxHeight: 500 }}>
          <Table size={"small"}>
            <EnhancedTableHead />
            <TableBody>
              {invoices.map((row, key) => {
                return (
                  <TableRow hover tabIndex={-1} key={key}>
                    <TableCell
                      className="Table-cell Table-cell-checkbox"
                      padding="checkbox"
                    >
                      <Checkbox
                        color="secondary"
                        onChange={() => selectInvoice(row.id)}
                        disabled={isSelectDisabled(row.status)}
                        defaultChecked={isSelectDisabled(row.status)}
                      />
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-invoice_number">
                      {key === editingRowId
                        ? getEditField(key, "invoiceNumber")
                        : row.invoiceNumber}
                    </TableCell>
                    <TableCell className="Table-cell">
                      {key === editingRowId
                        ? getEditField(key, "month")
                        : row.month}
                    </TableCell>
                    <TableCell className="Table-cell">
                      {key === editingRowId
                        ? getEditField(key, "invCostTypeId")
                        : getCostType(row)}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-amount">
                      {key === editingRowId
                        ? getEditField(key, "invoiceAmount")
                        : row.invoiceAmount}
                    </TableCell>
                    <TableCell className="Table-cell">
                      {key === editingRowId
                        ? getEditField(key, "invoiceCurrency")
                        : row.invoiceCurrency}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-amount">
                      {key === editingRowId
                        ? getEditField(key, "convertedAmount")
                        : row.convertedAmount}
                    </TableCell>
                    <TableCell className="Table-cell">
                      {key === editingRowId
                        ? getEditField(key, "convertedCurrency")
                        : row.convertedCurrency}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-hr-approval">
                      {key === editingRowId
                        ? getEditField(key, "hrApproval")
                        : getHrApprovalInfo(row)}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-finance-approval">
                      {key === editingRowId
                        ? getEditField(key, "financeApproval")
                        : getFinanceApprovalInfo(row)}
                    </TableCell>
                    <TableCell className="Table-cell Table-cell-net-suite-status">
                      {key === editingRowId
                        ? getEditField(key, "netSuiteStatus")
                        : getNetSuiteStatus(row)}
                    </TableCell>
                    {/* <TableCell className='Table-cell Table-cell-action'>
                                            <DeleteForeverIcon color='secondary' className='Remove-icon' onClick={() => deleteinvoices(row, key)}
                                                disabled={!isAccessGranted([PRIVILEGES.DELETE_invoices]) || !(travel.status === travelStatus.INPROGRESS || travel.status === travelStatus.EXTENDED)}
                                                style={{
                                                    color: (!isAccessGranted([PRIVILEGES.DELETE_invoices]) || !(travel.status === travelStatus.INPROGRESS || travel.status === travelStatus.EXTENDED))
                                                        ? wso2Theme.palette.disabled.main
                                                        : wso2Theme.palette.secondary.main
                                                }} />
                                        </TableCell> */}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!isLoading && invoices.length === 0 && (
            <div className="No-invoices-label">No Invoices</div>
          )}
        </TableContainer>
        <Grid container className="Button-grid">
          <Grid item lg={3} className="Button-div-add-invoice">
            {isAccessGranted([PRIVILEGES.ADD_TRAVEL_INVOICES]) && (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Add-invoice-button"
                disabled={
                  !(
                    travel.status === travelStatus.INPROGRESS ||
                    travel.status === travelStatus.EXTENDED ||
                    travel.status === travelStatus.CANCELLED
                  )
                }
                startIcon={<AddCircleOutlineIcon />}
                onClick={validateAndAddInvoice}
              >
                Add Invoice
              </Button>
            )}
          </Grid>
          {isAccessGranted([PRIVILEGES.ADD_TRAVEL_INVOICES]) && (
            <Grid item lg={9} className="Button-div-save-reset">
              <Button
                variant="contained"
                size="small"
                color="secondary"
                className="Save-button"
                disabled={!isSaveEnable || invoices.length === 0}
                onClick={saveInvoices}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className="Reset-button"
                disabled={!isResetEnable}
                onClick={resetInvoices}
              >
                Reset
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default Invoices;
